// src/pages/InsightGenerator.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, MenuItem, LinearProgress, Box, Slider, ListSubheader
} from "@mui/material";
import { ContentBuilderPage } from "../components/ContentBuilderPage";
import { useAxiosAuth } from "../hooks/useAxiosAuth";

/* --- Modal just handles form state --- */
function SelfQuizGeneratorModal({ open, onCancel, onGenerate, initialText }) {
  // NEW: keep the text in state so it can be edited
  const [textToVisualize, setTextToVisualize] = useState(initialText?.length > 0 ? initialText : "Quiz generation for complete lesson");
  const [vizType, setVizType] = useState("multiple_choice");
  const [vizSubtype, setVizSubtype] = useState("");
  const [vizName, setVizName] = useState("Quiz");
  const [loading, setLoading] = useState(false);
  const [quizScope, setQuizScope] = useState("selection");  // one of lesson, selection
  const MIN_WORDS = 400;
  const MAX_WORDS = 8000;

  const WHOLE_LESSON = "whole_lesson";

  const [numQuestions, setNumQuestions] = useState(5); 

  // If initialText ever changes from the parent, sync it
  useEffect(() => {
    setTextToVisualize(initialText?.length > 0 ? initialText : "Quiz generation for complete lesson");
    setQuizScope(initialText?.length > 0 ? "selection" : "lesson");
  }, [initialText]);

  const handleSubmit = async () => {
    setLoading(true);
    // now pass the edited text back up, too
    try {
      await onGenerate(textToVisualize, { vizName, vizType, vizSubtype, numQuestions, quizScope });
    } catch (e) {
      console.error(e)
      //setError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Quiz Builder</DialogTitle>
      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* scrollable text area */}
        <Box sx={{ flex: '1 1 auto', maxHeight: '40vh', overflowY: 'auto' }}>
          <TextField
            fullWidth
            multiline
            // no longer disabled
            value={textToVisualize}
            onChange={e => setTextToVisualize(e.target.value)}
            margin="normal"
            disabled={quizScope === "lesson"}
          />
        </Box>

        {/* sticky controls */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            pt: 2,
            pb: 1,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <TextField
            label="Quiz Name"
            fullWidth
            value={vizName}
            onChange={e => setVizName(e.target.value)}
            margin="normal"
          />
          <TextField
            select
            label="Quiz Type"
            fullWidth
            value={vizType}
            onChange={e => setVizType(e.target.value)}
            margin="normal"
          >
            <ListSubheader>Remember / Understand</ListSubheader>
            <MenuItem value="true_false">True or False</MenuItem>
            <MenuItem value="multiple_choice">Single-Select (Choose one)</MenuItem>
            <ListSubheader>Apply / Analyze</ListSubheader>
            <MenuItem value="multiselect">Multi-Select (Choose all that apply)</MenuItem>
            <MenuItem value="numeric">Numeric</MenuItem>
            <ListSubheader>Evaluate / Create</ListSubheader>
            <MenuItem value="short_answer">Short Answer</MenuItem>
            <MenuItem value="confidence_mcq">Confidence-Based Multiple Choice</MenuItem>
            <ListSubheader>Testing</ListSubheader>
            <MenuItem value="placeholder">Quiz Placeholder</MenuItem>
          </TextField>
          <TextField
            fullWidth
            type="number"
            value={numQuestions}
            onChange={(e) => setNumQuestions(parseInt(e.target.value, 10))}
            label="Number of Questions"
            variant="outlined"
            margin="normal"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}


/* --- Page component --- */
export default function SelfQuizGenerator() {
  const axios = useAxiosAuth();

  const generateVisualization = useCallback(
    async (text, courseId, lessonId, { vizName, vizType, vizSubtype, numQuestions, quizScope  }) => {
      const body = {
        concept: text,
        course_id: courseId,
        lesson_id: lessonId,
        quiz_type: vizType,
        num_questions: numQuestions,
        quiz_name: vizName,
        quiz_scope: quizScope
      };
      const { data } = await axios.post("/generate_quiz/", body);
      return data;
    },
    [axios]
  );

  const getAnchorLabel = useCallback((viz) => {
    const { vizType, vizName } = viz?.extraArgs ?? {};
    return "Open quiz in new tab";
  }, []);

  return (
    <ContentBuilderPage
      instructionText="Select a section of text to create quiz."
      modal={<SelfQuizGeneratorModal />}
      generatorFn={generateVisualization}
      getAnchorLabel={getAnchorLabel}
      assetType="quizzes"
      createBtnTxt="Create Quiz for Lesson"
    />
  );
}
